// src/routes/AppointmentList.jsx - PRIORITY FILTER ADDED
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiCalendar,
  FiFilter,
  FiX,
  FiList,
  FiClock,
  FiGrid,
  FiUser,
  FiPhone,
  FiCheck,
  FiXCircle,
  FiArrowLeft,
  FiAlertCircle, // ✅ NEW - For priority icon
} from "react-icons/fi";
import Button from "../../components/common/Button";
import {
  getAppointmentsThunk,
  approveAppointmentThunk,
  rejectAppointmentThunk,
} from "../../store/thunks/appointmentThunks";
import toast from "react-hot-toast";
import { setSelectedBranch } from "../../store/slices/branchSlice";

const AppointmentList = ({ view: routeView }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { appointments, loading } = useSelector((state) => state.appointment);
  const { selectedBranch, branches } = useSelector((state) => state.branch);

  // View mode: 'card' or 'table'
  const [displayMode, setDisplayMode] = useState("card");

  // ✅ UPDATED: Initialize filters from URL query parameters - ADDED priority
  const [filters, setFilters] = useState({
    date: searchParams.get("date") || "",
    startDate: searchParams.get("startDate") || "",
    endDate: searchParams.get("endDate") || "",
    status: searchParams.get("status") || "",
    userPhone: searchParams.get("userPhone") || "",
    userName: searchParams.get("userName") || "",
    adminCode: searchParams.get("adminCode") || "",
    appointmentType: searchParams.get("appointmentType") || "",
    purposeType: searchParams.get("purposeType") || "",
    priority: searchParams.get("priority") || "", // ✅ NEW
  });

  const [showFilters, setShowFilters] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedAppointmentForAction, setSelectedAppointmentForAction] =
    useState(null);

  // Apply view-based filters
  const getViewFilters = () => {
    const baseFilters = { ...filters };

    // Route view takes precedence only if no URL params
    const hasUrlFilters = Object.values(filters).some((v) => v);

    if (!hasUrlFilters && routeView === "today") {
      const today = new Date().toISOString().split("T")[0];
      return { ...baseFilters, date: today };
    } else if (!hasUrlFilters && routeView === "upcoming") {
      const today = new Date().toISOString().split("T")[0];
      const nextWeek = new Date();
      nextWeek.setDate(nextWeek.getDate() + 7);
      return {
        ...baseFilters,
        startDate: today,
        endDate: nextWeek.toISOString().split("T")[0],
      };
    } else if (!hasUrlFilters && routeView === "pending") {
      return { ...baseFilters, status: "PENDING" };
    }

    return baseFilters;
  };

  useEffect(() => {
    loadAppointments();
  }, [selectedBranch, filters, routeView]);

  // Update URL when filters change
  useEffect(() => {
    const params = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        params[key] = filters[key];
      }
    });

    setSearchParams(params, { replace: true });
  }, [filters]);

  const loadAppointments = () => {
    const viewFilters = getViewFilters();
    const params = {
      branchCode: selectedBranch,
      ...viewFilters,
    };

    // Remove empty filters
    Object.keys(params).forEach((key) => {
      if (!params[key]) delete params[key];
    });

    dispatch(getAppointmentsThunk(params));
  };

  const handleBranchChange = (branchCode) => {
    dispatch(setSelectedBranch(branchCode));
  };

  const handleApprove = async (appointmentId) => {
    try {
      await dispatch(
        approveAppointmentThunk({
          appointmentId,
          approvalData: {}, // Add any approval notes if needed
        })
      ).unwrap();
      toast.success("Appointment approved successfully");
      loadAppointments(); // Refresh list
    } catch (error) {
      toast.error(error || "Failed to approve appointment");
    }
  };

  const handleReject = async (reason, reasonDetails) => {
    if (!selectedAppointmentForAction) return;

    try {
      await dispatch(
        rejectAppointmentThunk({
          appointmentId: selectedAppointmentForAction._id,
          rejectionData: {
            reason,
            reasonDetails,
          },
        })
      ).unwrap();
      toast.success("Appointment rejected");
      setShowRejectModal(false);
      setSelectedAppointmentForAction(null);
      loadAppointments(); // Refresh list
    } catch (error) {
      toast.error(error || "Failed to reject appointment");
    }
  };

  const openRejectModal = (appointment) => {
    setSelectedAppointmentForAction(appointment);
    setShowRejectModal(true);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ✅ UPDATED: handleClearFilters - ADDED priority
  const handleClearFilters = () => {
    setFilters({
      date: "",
      startDate: "",
      endDate: "",
      status: "",
      userPhone: "",
      userName: "",
      adminCode: "",
      appointmentType: "",
      purposeType: "",
      priority: "", // ✅ NEW
    });
  };

  const hasActiveFilters = Object.values(filters).some((value) => value !== "");

  const handleAppointmentClick = (appointmentId) => {
    navigate(`/appointments/${appointmentId}`);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Format time for display
  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const colors = {
      PENDING: "bg-yellow-100 text-yellow-800 border-yellow-300",
      CONFIRMED: "bg-blue-100 text-blue-800 border-blue-300",
      COMPLETED: "bg-green-100 text-green-800 border-green-300",
      CANCELLED: "bg-red-100 text-red-800 border-red-300",
      NO_SHOW: "bg-gray-100 text-gray-800 border-gray-300",
    };
    return colors[status] || "bg-gray-100 text-gray-800 border-gray-300";
  };

  // ✅ NEW: Get priority badge color
  const getPriorityColor = (priority) => {
    const colors = {
      URGENT: "bg-red-100 text-red-800 border-red-300",
      HIGH: "bg-orange-100 text-orange-800 border-orange-300",
      MEDIUM: "bg-blue-100 text-blue-800 border-blue-300",
      LOW: "bg-gray-100 text-gray-800 border-gray-300",
    };
    return colors[priority] || "bg-gray-100 text-gray-800 border-gray-300";
  };

  // Format status for display
  const formatStatus = (status) => {
    return status?.replace(/_/g, " ") || "Unknown";
  };

  // Get view title
  const getViewTitle = () => {
    if (hasActiveFilters && filters.date)
      return `Appointments on ${formatDate(filters.date)}`;
    if (routeView === "today") return "Today's Appointments";
    if (routeView === "upcoming") return "Upcoming Appointments";
    if (routeView === "pending") return "Pending Appointments";
    if (hasActiveFilters) return "Filtered Appointments";
    return "All Appointments";
  };

  return (
    <div className="container mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/appointments")}
          className="flex items-center text-neutral-dark hover:text-primary mb-4 transition-colors"
        >
          <FiArrowLeft className="mr-2" />
          Back to Appointments Dashboard
        </button>

        <div className="flex flex-col gap-4 mb-4">
          {/* Title Row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-text mb-1">
                {getViewTitle()}
              </h1>
              <p className="text-neutral-dark">
                {appointments?.length || 0} appointment
                {appointments?.length !== 1 ? "s" : ""}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* Branch Selector - only show if admin has multiple branches */}
              {branches?.length > 1 && (
                <select
                  value={selectedBranch}
                  onChange={(e) => handleBranchChange(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All Branches</option>
                  {branches.map((branch) => (
                    <option key={branch.code} value={branch.code}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              )}

              {/* View Mode Toggle */}
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setDisplayMode("card")}
                  className={`px-3 py-2 ${
                    displayMode === "card"
                      ? "bg-primary text-white"
                      : "bg-white text-neutral-dark hover:bg-gray-50"
                  } transition-colors`}
                  title="Card View"
                >
                  <FiGrid size={18} />
                </button>
                <button
                  onClick={() => setDisplayMode("table")}
                  className={`px-3 py-2 border-l border-gray-300 ${
                    displayMode === "table"
                      ? "bg-primary text-white"
                      : "bg-white text-neutral-dark hover:bg-gray-50"
                  } transition-colors`}
                  title="Table View"
                >
                  <FiList size={18} />
                </button>
              </div>

              <Button
                variant={showFilters ? "primary" : "outline"}
                onClick={() => setShowFilters(!showFilters)}
                className="relative"
              >
                <FiFilter className="mr-2" />
                Filters
                {hasActiveFilters && (
                  <span className="absolute -top-1 -right-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {Object.values(filters).filter((v) => v).length}
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-neutral-dark font-medium">
                Active Filters:
              </span>
              {filters.status && (
                <FilterChip
                  label={`Status: ${formatStatus(filters.status)}`}
                  onRemove={() => handleFilterChange("status", "")}
                />
              )}
              {filters.appointmentType && (
                <FilterChip
                  label={`Type: ${formatStatus(filters.appointmentType)}`}
                  onRemove={() => handleFilterChange("appointmentType", "")}
                />
              )}
              {filters.purposeType && (
                <FilterChip
                  label={`Purpose: ${formatStatus(filters.purposeType)}`}
                  onRemove={() => handleFilterChange("purposeType", "")}
                />
              )}
              {/* ✅ NEW: Priority filter chip */}
              {filters.priority && (
                <FilterChip
                  label={`Priority: ${filters.priority}`}
                  onRemove={() => handleFilterChange("priority", "")}
                />
              )}
              {filters.date && (
                <FilterChip
                  label={`Date: ${formatDate(filters.date)}`}
                  onRemove={() => handleFilterChange("date", "")}
                />
              )}
              {filters.startDate && filters.endDate && (
                <FilterChip
                  label={`${formatDate(filters.startDate)} - ${formatDate(
                    filters.endDate
                  )}`}
                  onRemove={() => {
                    handleFilterChange("startDate", "");
                    handleFilterChange("endDate", "");
                  }}
                />
              )}
              {filters.userName && (
                <FilterChip
                  label={`Name: ${filters.userName}`}
                  onRemove={() => handleFilterChange("userName", "")}
                />
              )}
              {filters.userPhone && (
                <FilterChip
                  label={`Phone: ${filters.userPhone}`}
                  onRemove={() => handleFilterChange("userPhone", "")}
                />
              )}
              <button
                onClick={handleClearFilters}
                className="text-sm text-primary hover:text-primary-dark font-medium"
              >
                Clear All
              </button>
            </div>
          )}
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-4 border border-neutral-light">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={filters.date}
                  onChange={(e) => handleFilterChange("date", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="CONFIRMED">Confirmed</option>
                  <option value="CHECKED_IN">Checked In</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="NO_SHOW">No Show</option>
                </select>
              </div>

              {/* ✅ NEW: Priority Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  value={filters.priority}
                  onChange={(e) =>
                    handleFilterChange("priority", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Priorities</option>
                  <option value="URGENT">Urgent</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </div>

              {/* Appointment Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Appointment Type
                </label>
                <select
                  value={filters.appointmentType}
                  onChange={(e) =>
                    handleFilterChange("appointmentType", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Types</option>
                  <option value="PRODUCT_CONSULTATION">
                    Product Consultation
                  </option>
                  <option value="CUSTOM_DESIGN">Custom Design</option>
                  <option value="REPAIR_CONSULTATION">
                    Repair Consultation
                  </option>
                  <option value="APPRAISAL">Appraisal</option>
                  <option value="ENGAGEMENT_RING">Engagement Ring</option>
                  <option value="WEDDING_JEWELRY">Wedding Jewelry</option>
                  <option value="GENERAL_INQUIRY">General Inquiry</option>
                  <option value="VIP_CLIENT">VIP Client</option>
                  <option value="COLLECTION_VIEWING">Collection Viewing</option>
                </select>
              </div>

              {/* Purpose Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purpose
                </label>
                <select
                  value={filters.purposeType}
                  onChange={(e) =>
                    handleFilterChange("purposeType", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Purposes</option>
                  <option value="BROWSING">Browsing</option>
                  <option value="BUYING">Buying</option>
                  <option value="CUSTOM_DESIGN">Custom Design</option>
                  <option value="EXCHANGE_VALUATION">Exchange Valuation</option>
                  <option value="REPAIR_INQUIRY">Repair Inquiry</option>
                  <option value="GIFT_SHOPPING">Gift Shopping</option>
                  <option value="WEDDING_PLANNING">Wedding Planning</option>
                  <option value="INVESTMENT">Investment</option>
                </select>
              </div>

              {/* Customer Name Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Name
                </label>
                <input
                  type="text"
                  value={filters.userName}
                  onChange={(e) =>
                    handleFilterChange("userName", e.target.value)
                  }
                  placeholder="Search by name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Customer Phone Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Phone
                </label>
                <input
                  type="text"
                  value={filters.userPhone}
                  onChange={(e) =>
                    handleFilterChange("userPhone", e.target.value)
                  }
                  placeholder="Search by phone..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-neutral-light">
              <Button variant="outline" size="sm" onClick={handleClearFilters}>
                Clear Filters
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowFilters(false)}
              >
                Apply Filters
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : appointments && appointments.length > 0 ? (
        displayMode === "card" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {appointments.map((appointment) => (
              <AppointmentCard
                key={appointment._id}
                appointment={appointment}
                onClick={() => handleAppointmentClick(appointment._id)}
                onApprove={handleApprove}
                onReject={openRejectModal}
                formatDate={formatDate}
                formatTime={formatTime}
                formatStatus={formatStatus}
                getStatusColor={getStatusColor}
                getPriorityColor={getPriorityColor} // ✅ NEW
              />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Appointment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  {/* ✅ NEW: Priority column */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {appointments.map((appointment) => (
                  <tr
                    key={appointment._id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleAppointmentClick(appointment._id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {appointment.appointmentCode}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {appointment.userName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {appointment.userPhone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(appointment.scheduledDateTime)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatTime(appointment.scheduledDateTime)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {formatStatus(appointment.type)}
                      </span>
                    </td>
                    {/* ✅ NEW: Priority cell */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded border ${getPriorityColor(
                          appointment.priority
                        )}`}
                      >
                        {appointment.priority || "MEDIUM"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded border ${getStatusColor(
                          appointment.status
                        )}`}
                      >
                        {formatStatus(appointment.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {appointment.status === "PENDING" ? (
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApprove(appointment._id);
                            }}
                            className="text-green-600 hover:text-green-800"
                          >
                            <FiCheck size={18} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openRejectModal(appointment);
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <FiXCircle size={18} />
                          </button>
                        </div>
                      ) : (
                        <button className="text-primary hover:text-primary-dark">
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <FiCalendar className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No appointments found
          </h3>
          <p className="text-gray-500">
            {hasActiveFilters
              ? "Try adjusting your filters"
              : "No appointments scheduled yet"}
          </p>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <RejectModal
          onClose={() => {
            setShowRejectModal(false);
            setSelectedAppointmentForAction(null);
          }}
          onConfirm={handleReject}
          appointment={selectedAppointmentForAction}
        />
      )}
    </div>
  );
};

// ✅ UPDATED: Card Component - Added priority display
const AppointmentCard = ({
  appointment,
  onClick,
  onApprove,
  onReject,
  formatDate,
  formatTime,
  formatStatus,
  getStatusColor,
  getPriorityColor, // ✅ NEW prop
}) => {
  const isPending = appointment.status === "PENDING";

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-lg shadow hover:shadow-md transition-shadow ${
        !isPending ? "cursor-pointer" : ""
      } overflow-hidden border border-neutral-light`}
    >
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-gray-200 to-gray-200 p-4 text-primary">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs opacity-90 mb-1">Code</p>
            <p className="font-mono text-sm font-semibold truncate">
              {appointment.appointmentCode}
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <span
              className={`px-2 py-1 text-xs font-semibold rounded border flex-shrink-0 ${getStatusColor(
                appointment.status
              )}`}
            >
              {formatStatus(appointment.status)}
            </span>
            {/* ✅ NEW: Priority badge */}
            {appointment.priority && (
              <span
                className={`px-2 py-1 text-xs font-medium rounded border flex-shrink-0 ${getPriorityColor(
                  appointment.priority
                )}`}
              >
                {appointment.priority}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Date & Time - Prominent */}
        <div className="flex items-center gap-3">
          <div className="bg-primary-lighter p-2 rounded">
            <FiCalendar className="text-primary" size={20} />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-text">
              {formatDate(appointment.scheduledDateTime)}
            </p>
            <p className="text-sm text-neutral-dark">
              {formatTime(appointment.scheduledDateTime)} •{" "}
              {appointment.duration}min
            </p>
          </div>
        </div>

        <div className="border-t border-neutral-light pt-3">
          {/* Customer */}
          <div className="flex items-start gap-2 mb-2">
            <FiUser
              className="text-neutral-dark flex-shrink-0 mt-1"
              size={16}
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-text truncate">
                {appointment.userName}
              </p>
              <div className="flex items-center gap-1 text-sm text-neutral-dark mt-0.5">
                <FiPhone size={12} />
                <span>{appointment.userPhone}</span>
              </div>
            </div>
          </div>

          {/* Type & Purpose Tags */}
          <div className="flex flex-wrap gap-2 mt-3">
            <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded font-medium">
              {formatStatus(appointment.type)}
            </span>
            {appointment.intent?.purposeType && (
              <span className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded font-medium">
                {formatStatus(appointment.intent.purposeType)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Footer - Conditional based on status */}
      <div className="px-4 py-3 bg-neutral-lighter border-t border-neutral-light">
        {isPending ? (
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onApprove(appointment._id);
              }}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              <FiCheck className="mr-1" />
              Approve
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onReject(appointment);
              }}
              className="flex-1 border-red-600 text-red-600 hover:bg-red-50"
            >
              <FiXCircle className="mr-1" />
              Reject
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="text-xs text-neutral-dark">
              <span className="font-medium">{appointment.adminName}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
            >
              View
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Filter Chip Component
const FilterChip = ({ label, onRemove }) => (
  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary text-white rounded-full text-xs font-medium">
    <span>{label}</span>
    <button
      onClick={onRemove}
      className="hover:bg-primary-dark rounded-full p-0.5 transition-colors"
    >
      <FiX size={12} />
    </button>
  </div>
);

// Reject Modal Component
const RejectModal = ({ onClose, onConfirm, appointment }) => {
  const [reason, setReason] = useState("");
  const [reasonDetails, setReasonDetails] = useState("");

  const reasons = [
    { value: "UNAVAILABLE_TIME", label: "Time Slot Unavailable" },
    { value: "ADMIN_UNAVAILABLE", label: "Admin Unavailable" },
    { value: "BRANCH_CLOSED", label: "Branch Closed" },
    { value: "DUPLICATE_BOOKING", label: "Duplicate Booking" },
    { value: "INCOMPLETE_INFO", label: "Incomplete Information" },
    { value: "OTHER", label: "Other" },
  ];

  const handleSubmit = () => {
    if (!reason) {
      toast.error("Please select a reason");
      return;
    }
    onConfirm(reason, reasonDetails);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold text-text mb-4">Reject Appointment</h3>

        {appointment && (
          <div className="bg-neutral-lighter rounded-lg p-3 mb-4">
            <p className="text-sm font-medium text-text mb-1">
              {appointment.userName}
            </p>
            <p className="text-xs text-neutral-dark">
              {appointment.appointmentCode}
            </p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Rejection *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Select a reason</option>
              {reasons.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Details (Optional)
            </label>
            <textarea
              value={reasonDetails}
              onChange={(e) => setReasonDetails(e.target.value)}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Provide more details..."
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1 bg-red-600 hover:bg-red-700"
          >
            Reject Appointment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AppointmentList;
